import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Client } from "@microsoft/microsoft-graph-client";
import { AuthenticationResult } from "@azure/msal-browser";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  MenuItem,
  Alert,
  AlertTitle,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GraphPermissionsPicker, { GraphPermission } from "./GraphPermissionPicker";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import SecurityIcon from "@mui/icons-material/Security";
import { Chip, Tooltip } from "@mui/material";

/** Microsoft Graph well-known appId */
const MS_GRAPH_APP_ID = "00000003-0000-0000-c000-000000000000";

/** A single scope/role required by your app. */
interface ResourceAccess {
  id: string;
  type: "Scope" | "Role";
  friendlyName?: string;
}

/** The top-level structure describing a resource your app calls. */
interface RequiredResourceAccess {
  resourceAppId: string;
  resourceAccess: ResourceAccess[];
}

/**
 * Extend RequiredResourceAccess to include the new `resourceName` property
 */
interface ExtendedRequiredResourceAccess extends RequiredResourceAccess {
  resourceName?: string;
}



/** Minimal shape for friendly name resolution. */
/**
 * Extend your ServicePrincipalData to include appDisplayName/displayName
 */
interface ServicePrincipalData {
  appRoles: {
    id: string;
    displayName?: string;
    value?: string;
  }[];
  oauth2PermissionScopes: {
    id: string;
    displayName?: string;
    value?: string;
  }[];
  appDisplayName?: string;
  displayName?: string;
}

/** Props for our manager. */
interface ApiPermissionsManagerProps {
  appId: string; // e.g. your Application’s objectId or appId
  clientServicePrincipalId: string; // The SP ID of your client app
  initialRequiredResourceAccess: RequiredResourceAccess[];
  updateApplication: (updatedRra: RequiredResourceAccess[]) => Promise<void>;
}

/**
 * A manager that merges all your old code for:
 * - Adding Graph perms (manual + GraphPermissionsPicker)
 * - Adding Custom perms
 * - Grant Admin Consent
 * - Apply Changes
 * - Color-coded blocks
 */
const ApiPermissionsManager: React.FC<ApiPermissionsManagerProps> = ({
  appId,
  clientServicePrincipalId,
  initialRequiredResourceAccess,
  updateApplication,
}) => {
  const { instance, accounts } = useMsal();

  // The local “master” array of permissions your app requires.
  const [requiredResourceAccess, setRequiredResourceAccess] = useState<RequiredResourceAccess[]>(
    initialRequiredResourceAccess
  );
  // A version with friendly names (from resource SP).
  const [resolvedRequiredResourceAccess, setResolvedRequiredResourceAccess] = 
  useState<ExtendedRequiredResourceAccess[]>([]);

  // Track admin consent per resource.
  const [adminConsentMap, setAdminConsentMap] = useState<Record<string, boolean>>({});

  // Loading spinner.
  const [loading, setLoading] = useState(false);

  const [graphApiDialogOpen, setGraphApiDialogOpen] = useState(false);

  // Graph perms picking UI
  const [graphPermsOpen, setGraphPermsOpen] = useState(false);
  const [graphAccessToken, setGraphAccessToken] = useState("");

  // “Custom API” add form (dialog)
  const [customApiDialogOpen, setCustomApiDialogOpen] = useState(false);
  const [customApiResourceAppId, setCustomApiResourceAppId] = useState("");
  const [customApiScopeId, setCustomApiScopeId] = useState("");

  // “Manual Add Graph” text fields, like your old code
  const [newApiResourceAppId, setNewApiResourceAppId] = useState("");
  const [newApiScopeId, setNewApiScopeId] = useState("");

  /** Acquire Graph token with Directory.Read.All */
  const getGraphToken = async (): Promise<string> => {
    const request = { scopes: ["Directory.Read.All"], account: accounts[0] };
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  };

  // State to control expansion
  const [graphApiExpanded, setGraphApiExpanded] = useState(false);
  const [customApiExpanded, setCustomApiExpanded] = useState(false);

  // On local changes => re-resolve names + admin consent
  useEffect(() => {
    void resolveFriendlyNames(requiredResourceAccess);
    void refreshAdminConsentStatus(requiredResourceAccess);
  }, [requiredResourceAccess]);

  // NEW - added for facelifting
  const [showGraphPermissionReminder, setShowGraphPermissionReminder] = useState<boolean>(false);
  const [showCustomPermissionReminder, setShowCustomPermissionReminder] = useState<boolean>(false);
  const [isAddingPermission, setIsAddingPermission] = useState<boolean>(true);
  const [isAddingCustomPermission, setIsAddingCustomPermission] = useState<boolean>(true);

  const [customScopeType, setCustomScopeType] = useState<"Scope" | "Role">("Scope");
  const [lookupApiId, setLookupApiId] = useState("");
  const [availableScopes, setAvailableScopes] = useState<Array<{id: string, value: string, type: "Scope" | "Role"}>>([]);
  const [isLookingUpScopes, setIsLookingUpScopes] = useState(false);
  const [customApiResourceName, setCustomApiResourceName] = useState<string>("");

  /**
   * 1) Resolves friendly names for each resource access. 
   * Also adds a `resourceName` field with a friendly name for the overall resource.
   */
  const resolveFriendlyNames = async (rraList: RequiredResourceAccess[]) => {
    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token),
      });

      const updated: ExtendedRequiredResourceAccess[] = [];

      for (const resource of rraList) {
        let spData: ServicePrincipalData | null = null;
        try {
          const spResp = await client
            .api(`/servicePrincipals?$filter=appId eq '${resource.resourceAppId}'`)
            .get();
          if (spResp.value && spResp.value.length > 0) {
            spData = spResp.value[0];
          }
        } catch (err) {
          console.warn(`SP not found for resourceAppId=${resource.resourceAppId}`, err);
        }

        // Fallback if not found:
        if (!spData) {
          updated.push({
            ...resource,
            resourceName: resource.resourceAppId, // fallback
          });
          continue;
        }

        // Build friendly overall resourceName
        const displayName =
          spData.appDisplayName ||
          spData.displayName ||
          `Resource ${resource.resourceAppId}`;

        // Build maps for role/scope display names
        const rolesMap = new Map(
          (spData.appRoles || []).map((r) => [
            r.id,
            r.displayName || r.value || "Unnamed Role",
          ])
        );
        const scopesMap = new Map(
          (spData.oauth2PermissionScopes || []).map((s) => [
            s.id,
            s.value || s.displayName || "Unnamed Scope",
          ])
        );

        // Add friendly names inside each resourceAccess item
        const newRA = resource.resourceAccess.map((ra) => {
          if (ra.type === "Scope" && scopesMap.has(ra.id)) {
            return { ...ra, friendlyName: scopesMap.get(ra.id) };
          } else if (ra.type === "Role" && rolesMap.has(ra.id)) {
            return { ...ra, friendlyName: rolesMap.get(ra.id) };
          }
          return ra;
        });

        updated.push({
          ...resource,
          resourceName: displayName,
          resourceAccess: newRA,
        });
      }

      setResolvedRequiredResourceAccess(updated);
    } catch (err) {
      console.error("Error in resolveFriendlyNames:", err);
      // fallback to original if resolution fails
      setResolvedRequiredResourceAccess(
        rraList.map((rra) => ({ ...rra, resourceName: rra.resourceAppId }))
      );
    }
  };

  // Update the lookupScopesForApi function to capture more scope details

  const lookupScopesForApi = async () => {
    if (!lookupApiId) return;
    
    setIsLookingUpScopes(true);
    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token)
      });
      
      // Look up the service principal for this app ID
      const spResp = await client
        .api(`/servicePrincipals?$filter=appId eq '${lookupApiId}'`)
        .select("id,appId,oauth2PermissionScopes,appRoles,displayName")
        .get();
      
      if (!spResp.value?.length) {
        alert("No service principal found for this AppID. Make sure it exists in your tenant.");
        return;
      }
      
      const sp = spResp.value[0];
      
      // Store the service principal display name for later use
      const spDisplayName = sp.displayName || lookupApiId;
      
      // Gather available scopes and roles with more details
      const scopesList = (sp.oauth2PermissionScopes || []).map((s: any) => ({
        id: s.id,
        value: s.value || s.displayName || "(unnamed)",
        displayName: s.displayName || s.value || "(unnamed)",
        description: s.description || "",
        type: "Scope" as const
      }));
      
      const rolesList = (sp.appRoles || []).map((r: any) => ({
        id: r.id,
        value: r.value || r.displayName || "(unnamed)",
        displayName: r.displayName || r.value || "(unnamed)",
        description: r.description || "",
        type: "Role" as const
      }));
      
      const allOptions = [...scopesList, ...rolesList];
      setAvailableScopes(allOptions);
      
      // If we found options, set the API ID and store the display name
      if (allOptions.length > 0) {
        setCustomApiResourceAppId(lookupApiId);
        // Store the service principal name in state for display
        setCustomApiResourceName(spDisplayName);
      }
      
    } catch (error) {
      console.error("Error looking up scopes:", error);
      alert("Failed to look up scopes for this API.");
    } finally {
      setIsLookingUpScopes(false);
    }
  };


  // =========================
  // 2) Admin Consent Check
  // =========================
  const refreshAdminConsentStatus = async (rraList: RequiredResourceAccess[]) => {
    setLoading(true);
    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token),
      });

      const newMap: Record<string, boolean> = {};
      for (const resource of rraList) {
        const spResp = await client
          .api(`/servicePrincipals?$filter=appId eq '${resource.resourceAppId}'`)
          .get();
        if (!spResp.value?.length) {
          newMap[resource.resourceAppId] = false;
          continue;
        }
        const resourceSpId = spResp.value[0].id;

        // If any tenant-wide grant => admin consent is done
        const grantsResp = await client
          .api(
            `/oauth2PermissionGrants?$filter=clientId eq '${clientServicePrincipalId}' AND resourceId eq '${resourceSpId}' AND consentType eq 'AllPrincipals'`
          )
          .get();
        newMap[resource.resourceAppId] = (grantsResp.value?.length || 0) > 0;
      }

      setAdminConsentMap(newMap);
    } catch (err) {
      console.error("Error refreshing admin consent:", err);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // 3) Add / Remove Perms
  // =========================
  /** GraphPermissionsPicker => handleGraphPermsSubmit */
  const handleGraphPermsSubmit = (selectedPerms: GraphPermission[]) => {
    const msGraphAppId = MS_GRAPH_APP_ID;
    const existingResource = requiredResourceAccess.find((r) => r.resourceAppId === msGraphAppId);

    let newList = existingResource?.resourceAccess
      ? [...existingResource.resourceAccess]
      : [];

    for (const perm of selectedPerms) {
      newList.push({
        id: perm.id,
        type: perm.type === "Scope" ? "Scope" : "Role",
      });

      setIsAddingPermission(true);
      setShowGraphPermissionReminder(true);
      
      // Auto-hide after 6 seconds
      setTimeout(() => {
        setShowGraphPermissionReminder(false);
      }, 6000);
    }

    if (!existingResource) {
      // add new resource
      setRequiredResourceAccess((prev) => [
        ...prev,
        { resourceAppId: msGraphAppId, resourceAccess: newList },
      ]);
    } else {
      // update existing
      setRequiredResourceAccess((prev) =>
        prev.map((r) =>
          r.resourceAppId === msGraphAppId ? { ...r, resourceAccess: newList } : r
        )
      );
    }
    setGraphPermsOpen(false);
  };

  /** Manual “Add Graph API Permission” from text fields for resourceAppId + scope */
  function handleAddGraphApiPermission() {
    if (!newApiResourceAppId || !newApiScopeId) {
      alert("Please fill out Resource AppId + Scope/Role ID");
      return;
    }
  
    const existingResource = requiredResourceAccess.find(
      (r) => r.resourceAppId === newApiResourceAppId
    );
  
    if (existingResource) {
      // Use "as const" so TS recognizes "Scope" as the literal type
      const newArr = [
        ...existingResource.resourceAccess, 
        { id: newApiScopeId, type: "Scope" as const },
      ];
      setRequiredResourceAccess((prev) =>
        prev.map((r) =>
          r.resourceAppId === newApiResourceAppId ? { ...r, resourceAccess: newArr } : r
        )
      );
    } else {
      // create a new resource
      const newItem: RequiredResourceAccess = {
        resourceAppId: newApiResourceAppId,
        resourceAccess: [{ id: newApiScopeId, type: "Scope" as const }],
      };
      setRequiredResourceAccess((prev) => [...prev, newItem]);
    }
  
    setNewApiResourceAppId("");
    setNewApiScopeId("");

    // Show reminder to apply changes
    setIsAddingPermission(true);
    setShowGraphPermissionReminder(true);
    
    // Auto-hide after 6 seconds
    setTimeout(() => {
    setShowGraphPermissionReminder(false);
  }, 6000);
  }
  

// Update the handleAddCustomApiPermission function to store scope values

function handleAddCustomApiPermission() {
  if (!customApiResourceAppId || !customApiScopeId) {
    alert("Please select both a resource and a permission");
    return;
  }
  
  // Find the selected scope's details
  const selectedScope = availableScopes.find(scope => scope.id === customApiScopeId);
  
  if (!selectedScope) {
    alert("Selected scope not found in available scopes");
    return;
  }
  
  const existingResource = requiredResourceAccess.find(
    (r) => r.resourceAppId === customApiResourceAppId
  );

  // Create the resource access object with friendly name
  const resourceAccess = { 
    id: customApiScopeId, 
    type: customScopeType,
    friendlyName: selectedScope.value // Store the scope value for display
  };

  if (existingResource) {
    const newArr = [
      ...existingResource.resourceAccess, 
      resourceAccess
    ];
    setRequiredResourceAccess((prev) =>
      prev.map((r) =>
        r.resourceAppId === customApiResourceAppId
          ? { ...r, resourceAccess: newArr }
          : r
      )
    );
  } else {
    const newItem: RequiredResourceAccess = {
      resourceAppId: customApiResourceAppId,
      resourceAccess: [resourceAccess],
    };
    setRequiredResourceAccess((prev) => [...prev, newItem]);
  }

  // Reset fields
  setCustomApiResourceAppId("");
  setCustomApiScopeId("");
  setAvailableScopes([]);
  setLookupApiId("");
  setCustomApiDialogOpen(false);

  // Show reminder to apply changes
  setIsAddingCustomPermission(true);
  setShowCustomPermissionReminder(true);
  
  // Auto-hide after 10 seconds
  setTimeout(() => {
    setShowCustomPermissionReminder(false);
  }, 10000);
}
  
  /** Remove a single scope/role from a resource. */
  function handleRemoveApiPermission(resourceAppId: string, accessId: string) {
    // First determine if this is a Graph or Custom API permission
    const isGraphPermission = resourceAppId === MS_GRAPH_APP_ID;
    
    setRequiredResourceAccess((prev) =>
      prev
        .map((rra) => {
          if (rra.resourceAppId === resourceAppId) {
            return {
              ...rra,
              resourceAccess: rra.resourceAccess.filter((ra) => ra.id !== accessId),
            };
          }
          return rra;
        })
        .filter((rra) => rra.resourceAccess.length > 0)
    );
    
    // Set the appropriate alert based on which type of permission was removed
    if (isGraphPermission) {
      setIsAddingPermission(false);
      setShowGraphPermissionReminder(true);
      
      // Auto-hide after 10 seconds
      setTimeout(() => {
        setShowGraphPermissionReminder(false);
      }, 10000);
    } else {
      setIsAddingCustomPermission(false);
      setShowCustomPermissionReminder(true);
      
      // Auto-hide after 10 seconds
      setTimeout(() => {
        setShowCustomPermissionReminder(false);
      }, 10000);
    }
  }

  // =========================
  // 4) Grant Admin Consent with partial coverage logic
  // =========================
  async function grantAdminConsentForAllResources() {
    setLoading(true);
    try {
      // Acquire token with fallback to interactive
      const request = {
        scopes: ["Application.ReadWrite.All", "AppRoleAssignment.ReadWrite.All"],
        account: accounts[0],
      };
      let tokenResp: AuthenticationResult | null = null;
      try {
        tokenResp = await instance.acquireTokenSilent(request);
      } catch (error: any) {
        if (error.name === "InteractionRequiredAuthError") {
          tokenResp = await instance.acquireTokenPopup(request);
        } else {
          throw error;
        }
      }
      if (!tokenResp) {
        throw new Error("No token acquired.");
      }
      
      // Now TypeScript knows tokenResp is non-null within this block
      const { accessToken } = tokenResp;
      
      const graphClient = Client.init({
        authProvider: (done) => done(null, accessToken),
      });
      
      let allConsentedAlready = true;
      // Initialize updatedConsentMap as a copy of the current adminConsentMap
      const updatedConsentMap = { ...adminConsentMap };
  
      for (const resource of requiredResourceAccess) {
        // 1) Find resource SP
        const spResp = await graphClient
          .api(`/servicePrincipals?$filter=appId eq '${resource.resourceAppId}'`)
          .get();
        if (!spResp.value?.length) {
          console.warn(`No SP found for resourceAppId=${resource.resourceAppId}`);
          continue;
        }
        const resourceSp = spResp.value[0];
        const resourceSpId = resourceSp.id;
  
        // 2) Check existing grants => might be partial coverage
        const existingGrantsResp = await graphClient
          .api(
            `/oauth2PermissionGrants?$filter=clientId eq '${clientServicePrincipalId}' AND resourceId eq '${resourceSpId}' AND consentType eq 'AllPrincipals'`
          )
          .get();
        // We'll only consider the first. If you have multiple, you might want to handle all.
        const existingGrant = existingGrantsResp.value && existingGrantsResp.value[0];
        // Collect newly requested scopes vs roles
        const scopeIds: string[] = [];
        const roleIds: string[] = [];
        for (const ra of resource.resourceAccess) {
          if (ra.type === "Scope") scopeIds.push(ra.id);
          else if (ra.type === "Role") roleIds.push(ra.id);
        }
  
        // 2a) If any existing grant covers some old scopes => we find missing scopes. 
        const spScopes = resourceSp.oauth2PermissionScopes || [];
  
        // Build an array of "newly added scope values"
        const requestedScopeValues = scopeIds.map((sid) => {
          const foundScope = spScopes.find((s: any) => s.id === sid);
          if (!foundScope) {
            console.warn(`Scope with ID ${sid} not found in resource. Possibly a mismatch?`);
          }
          return foundScope?.value || null;
        });
  
        // Remove .filter(Boolean) so nothing is silently dropped.
        // Instead, we keep them and let you handle null values explicitly.
        const validScopes = requestedScopeValues.filter((val) => val !== null);
  
        let missingScopes: string[] = validScopes; // default = all missing
  
        if (existingGrant?.scope) {
          // existingGrant.scope might be "User.Read Mail.Read"
          const alreadyGrantedScopes = existingGrant.scope.split(" ");
          // filter out those already covered
          missingScopes = requestedScopeValues.filter((val) => !alreadyGrantedScopes.includes(val));
        }
  
        if (missingScopes.length === 0 && roleIds.length === 0) {
          console.log(`Admin consent for resource ${resource.resourceAppId} is fully covered.`);
          updatedConsentMap[resource.resourceAppId] = true;
          continue; // skip
        } else {
          // We know there's at least some missing coverage => we'll set allConsentedAlready = false
          allConsentedAlready = false;
        }
  
        // 3) For the missing delegated scopes => new POST /oauth2PermissionGrants
        if (missingScopes.length > 0) {
          try {
            // Define newScopeString here - join the missing scopes with spaces
            const newScopeString = missingScopes.join(' ');
            
            await graphClient.api("/oauth2PermissionGrants").post({
              clientId: clientServicePrincipalId,
              resourceId: resourceSpId,
              scope: newScopeString,
              consentType: "AllPrincipals",
              principalId: null,
            });
            
            // Update the consent map immediately
            updatedConsentMap[resource.resourceAppId] = true;
            
          } catch (error: any) {
            if (error.statusCode === 409) {
              // 409 indicates the scope is already recorded => still consider it granted
              updatedConsentMap[resource.resourceAppId] = true;
              console.warn("Some scopes were already granted. Ignoring 409 conflict.");
            } else {
              throw error; // rethrow other errors
            }
          }
        }
  
        // 4) For application roles => see which ones are missing from /appRoleAssignments
        if (roleIds.length > 0) {
          try {
            // Modify this line - remove the quotes around resourceSpId
            const existingAssignmentsResp = await graphClient
              .api(`/servicePrincipals/${clientServicePrincipalId}/appRoleAssignments?$filter=resourceId eq ${resourceSpId}`)
              .get();
            
            const assignedRoleIds = (existingAssignmentsResp.value || []).map((x: any) => x.appRoleId);
  
            // find the roles not assigned yet
            const missingRoles = roleIds.filter((rid) => !assignedRoleIds.includes(rid));
  
            if (missingRoles.length === 0) {
              console.log(`All application roles are already assigned for resourceAppId=${resource.resourceAppId}`);
              updatedConsentMap[resource.resourceAppId] = true;
            } else {
              for (const rid of missingRoles) {
                console.log(`Assigning new appRoleId=${rid} => resourceSpId=${resourceSpId}`);
                await graphClient
                  .api(`/servicePrincipals/${clientServicePrincipalId}/appRoleAssignments`)
                  .post({
                    principalId: clientServicePrincipalId,
                    resourceId: resourceSpId,
                    appRoleId: rid,
                  });
              }
              // Mark as granted after successful assignments
              updatedConsentMap[resource.resourceAppId] = true;
            }
          } catch (err) {
            console.error(`Error processing app roles for resource ${resourceSpId}:`, err);
            // Continue with other resources rather than failing completely
          }
        }
      }
      
      // Update the admin consent map with our tracked changes
      setAdminConsentMap(updatedConsentMap);
  
      if (allConsentedAlready) {
        alert("✅ Admin consent was already granted for all required permissions.");
      } else {
        alert("✅ Admin consent process completed successfully (including newly added perms)!");
      }
      
      // Run a refresh afterward just to be sure we're in sync
      await refreshAdminConsentStatus(requiredResourceAccess);
    } catch (err) {
      console.error("Error granting admin consent:", err);
      alert("⚠️ Failed to grant admin consent. Check console.");
    } finally {
      setLoading(false);
    }
  }


  // =========================
  // 5) “Apply Changes” => calls updateApplication
  // =========================
// Update the handleApplyChanges function to clean the data before sending to Graph API

async function handleApplyChanges() {
  setLoading(true);
  try {
    // Clean the requiredResourceAccess before sending to Graph API
    const cleanedResourceAccess = requiredResourceAccess.map(resource => ({
      resourceAppId: resource.resourceAppId,
      resourceAccess: resource.resourceAccess.map(access => ({
        id: access.id,
        type: access.type
        // Removing friendlyName and any other UI-only properties
      }))
    }));
    
    await updateApplication(cleanedResourceAccess);
    alert("✅ API Permissions have been updated!");
    // re-check admin consent status after saving
    await refreshAdminConsentStatus(requiredResourceAccess);
  } catch (err) {
    console.error("Error applying changes:", err);
    alert("Failed to update. Check console.");
  } finally {
    // Clear all reminders
    setShowGraphPermissionReminder(false);
    setShowCustomPermissionReminder(false);
    setLoading(false);
  }
}

  // =========================
  // UI Rendering
  // =========================

  // Separate Graph from “other” resources
  const graphResources = resolvedRequiredResourceAccess.filter(
    (r) => r.resourceAppId === MS_GRAPH_APP_ID
  );
  const customResources = resolvedRequiredResourceAccess.filter(
    (r) => r.resourceAppId !== MS_GRAPH_APP_ID
  );

  // Update the renderResourceList function to display more permission details
  function renderResourceList(resources: ExtendedRequiredResourceAccess[]) {
    if (resources.length === 0) {
      return (
        <Box sx={{ 
          p: 2, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 1, 
          textAlign: 'center',
          border: '1px dashed #ccc'
        }}>
          <Typography variant="body1" color="text.secondary">
            No API permissions configured yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Use the buttons below to add permissions.
          </Typography>
        </Box>
      );
    }

    return resources.map((rra) => {
      const isGranted = adminConsentMap[rra.resourceAppId] === true;

      return (
        <Box key={rra.resourceAppId} sx={{ mb: 3 }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#ffffff', 
            border: '1px solid #e0e0e0', 
            borderRadius: 1,
            mb: 1
          }}>
            <Typography variant="subtitle1">
              <strong>{rra.resourceName ?? rra.resourceAppId}</strong>{" "}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Resource AppId: {rra.resourceAppId}<br />
              Admin Consent: <span style={{ color: isGranted ? 'green' : 'red', fontWeight: 'bold' }}>
                {isGranted ? "Granted ✓" : "Not Granted ✗"}
              </span>
            </Typography>

            <List sx={{ mt: 1, bgcolor: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 1 }}>
              {rra.resourceAccess.map((ra) => (
                <ListItem
                  key={ra.id}
                  sx={{
                    borderBottom: "1px solid #f0f0f0",
                    py: 1
                  }}
                  secondaryAction={
                    <Tooltip title="Remove permission">
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleRemoveApiPermission(rra.resourceAppId, ra.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {ra.friendlyName || ra.id}
                        </Typography>
                        <Chip 
                          size="small" 
                          label={ra.type} 
                          color={ra.type === "Role" ? "secondary" : "primary"}
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          <strong>Permission ID:</strong> {ra.id}
                        </Typography>
                        {/* {ra.friendlyName && (
                          <Typography variant="caption" display="block">
                            <strong>Scope:</strong> {ra.friendlyName}
                          </Typography>
                        )} */}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      );
    });
  }
  

  return (
    <Card variant="outlined">
      <CardContent>
        {/* <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          API Permissions
        </Typography> */}
        {loading && <CircularProgress />}
  
        {/* ============================== */}
        {/* (A) Microsoft Graph API Section */}
        {/* ============================== */}
        <Accordion 
          expanded={graphApiExpanded} 
          onChange={() => setGraphApiExpanded(!graphApiExpanded)}
          sx={{
            border: '1px solid #e8eaf6', // Light indigo border
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            mb: 2
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)', // Indigo gradient
              borderBottom: graphApiExpanded ? '1px solid #c5cae9' : 'none',
              minHeight: '56px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                backgroundColor: '#3f51b5', // Indigo background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}>
                <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "#283593", // Dark indigo text
                  letterSpacing: "0.01em"
                }}
              >
                Microsoft Graph API Permissions
              </Typography>
              <Chip 
                size="small"
                label="Microsoft Graph" 
                sx={{ 
                  ml: 2,
                  backgroundColor: '#c5cae9', // Light indigo background
                  color: '#283593', // Dark indigo text
                  fontWeight: 500
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
          {/* Save Reminder Alert for Graph permissions */}
            {showGraphPermissionReminder && (
              <Box sx={{ 
                p: 1.5, 
                mb: 2,
                backgroundColor: '#fff3e0', 
                borderLeft: '4px solid #ff9800',
                borderRadius: '0 4px 4px 0',
                display: 'flex',
                alignItems: 'center',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <Box sx={{ mr: 1.5, color: '#f57c00' }}>
                  <InfoOutlinedIcon />
                </Box>
                <Typography variant="body2">
                  <strong>Permission {isAddingPermission ? 'added' : 'removed'}!</strong> Remember to click <strong>Apply Changes</strong> at the bottom of the page to save your modifications.
                </Typography>
              </Box>
            )}

            {/* Render existing Graph perms with enhanced styling */}
            {graphResources.length === 0 ? (
            <Box sx={{ 
              p: 4, 
              backgroundColor: '#f8f9fa', 
              borderRadius: '12px', 
              textAlign: 'center',
              border: '2px dashed #dee2e6',
              mb: 2
            }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: '#e9ecef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No Microsoft Graph permissions configured yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the buttons below to add Graph API permissions
              </Typography>
            </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                {renderResourceList(graphResources)}
              </Box>
            )}
  
            {/* Buttons with improved styling */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button 
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setGraphApiDialogOpen(true)}
              >
                Add Permission Manually
              </Button>
              <Typography>OR</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={async () => {
                  try {
                    const token = await getGraphToken();
                    setGraphAccessToken(token);
                    setGraphPermsOpen(true);
                  } catch (err) {
                    console.error("Failed to get token:", err);
                    alert("Failed to get Graph token. See console.");
                  }
                }}
              >
                Pick from Microsoft Graph
              </Button>
            </Stack>
            
            {/* Apply Changes Button */}
            {/* {showGraphPermissionReminder && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mt: 2, 
                pt: 1, 
                borderTop: '1px solid #e0e0e0' 
              }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleApplyChanges}
                >
                  Apply Changes
                </Button>
              </Box>
            )} */}

          </AccordionDetails>
        </Accordion>
  
        {/* ============================== */}
        {/* (B) Custom API Section - Updated with consistent styling */}
        {/* ============================== */}
        <Accordion 
          expanded={customApiExpanded} 
          onChange={() => setCustomApiExpanded(!customApiExpanded)}
          sx={{
            border: '1px solid #e8eaf6', // Light indigo border to match other sections
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            mb: 2
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)', // Indigo gradient
              borderBottom: customApiExpanded ? '1px solid #c5cae9' : 'none',
              minHeight: '56px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                backgroundColor: '#3f51b5', // Indigo background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}>
                <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "#283593", // Dark indigo text
                  letterSpacing: "0.01em"
                }}
              >
                Custom API Permissions
              </Typography>
              <Chip 
                size="small"
                label="Custom APIs" 
                sx={{ 
                  ml: 2,
                  backgroundColor: '#c5cae9', // Light indigo background
                  color: '#283593', // Dark indigo text
                  fontWeight: 500
                }}
              />
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
            {/* Save Reminder Alert for Custom permissions */}
            {showCustomPermissionReminder && (
              <Box sx={{ 
                p: 1.5, 
                mb: 2,
                backgroundColor: '#fff3e0', 
                borderLeft: '4px solid #ff9800',
                borderRadius: '0 4px 4px 0',
                display: 'flex',
                alignItems: 'center',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <Box sx={{ mr: 1.5, color: '#f57c00' }}>
                  <InfoOutlinedIcon />
                </Box>
                <Typography variant="body2">
                  <strong>Custom permission {isAddingCustomPermission ? 'added' : 'removed'}!</strong> Remember to click <strong>Apply Changes</strong> at the bottom of the page to save your modifications.
                </Typography>
              </Box>
            )}

            {/* Enhanced empty state for Custom permissions */}
            {customResources.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                backgroundColor: '#f8f9fa', 
                borderRadius: '12px', 
                textAlign: 'center',
                border: '2px dashed #dee2e6',
                mb: 2
              }}>
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No custom API permissions configured yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the button below to add custom API permissions
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                {/* Enhanced permission list rendering */}
                {customResources.map((rra) => {
                  const isGranted = adminConsentMap[rra.resourceAppId] === true;

                  return (
                    <Box key={rra.resourceAppId} sx={{ mb: 3 }}>
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e8eaf6', // Indigo border
                        borderRadius: '12px', // Rounded corners
                        mb: 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
                              {rra.resourceName ?? rra.resourceAppId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Resource AppId: {rra.resourceAppId}
                            </Typography>
                            <Chip 
                              size="small"
                              label={isGranted ? "Admin Consent Granted ✓" : "Admin Consent Required ✗"}
                              sx={{ 
                                backgroundColor: isGranted ? '#e8f5e9' : '#ffebee',
                                color: isGranted ? '#2e7d32' : '#d32f2f',
                                fontWeight: 500,
                                border: `1px solid ${isGranted ? '#c8e6c9' : '#ffcdd2'}`
                              }}
                            />
                          </Box>
                        </Box>

                        <List sx={{ 
                          bgcolor: "#fafbff", 
                          border: "1px solid #e8eaf6", // Indigo border
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          {rra.resourceAccess.map((ra, index) => (
                            <ListItem
                              key={ra.id}
                              sx={{
                                borderBottom: index < rra.resourceAccess.length - 1 ? "1px solid #f0f0f0" : "none",
                                '&:hover': { backgroundColor: '#f5f5ff' }, // Light indigo hover
                                py: 1.5
                              }}
                              secondaryAction={
                                <Tooltip title="Remove permission">
                                  <IconButton
                                    edge="end"
                                    color="error"
                                    onClick={() => handleRemoveApiPermission(rra.resourceAppId, ra.id)}
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: '#ffebee'
                                      }
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              }
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" fontWeight="medium" sx={{ mr: 1 }}>
                                      {ra.friendlyName || ra.id}
                                    </Typography>
                                    <Chip 
                                      size="small" 
                                      label={ra.type} 
                                      color={ra.type === "Role" ? "secondary" : "primary"}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      <strong>Permission ID:</strong> {ra.id}
                                    </Typography>
                                    {ra.friendlyName && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        <strong>Permission Value:</strong> {ra.friendlyName}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Enhanced action button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-start', 
              alignItems: 'center',
              pt: 2, 
              borderTop: '1px solid #e8eaf6' // Indigo border
            }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCustomApiDialogOpen(true)}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: '#3f51b5', // Indigo border
                  color: '#3f51b5', // Indigo text
                  '&:hover': {
                    borderColor: '#283593',
                    backgroundColor: '#f3e5f5'
                  }
                }}
              >
                Add Custom API Permission
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

      {/* ============================== */}
      {/* (C) ABOUT API PERMISSIONS */}
      {/* ============================== */}
      <Accordion
        sx={{
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
          opacity: 0.85
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />}
          sx={{
            background: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            minHeight: '48px',
            '&:hover': { backgroundColor: '#f1f3f4' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} />
            <Typography 
              variant="subtitle1"
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "0.95rem",
                fontWeight: 500,
                letterSpacing: "0.005em"
              }}
            >
              About API Permissions
            </Typography>
            <Chip 
              size="small"
              label="Info" 
              variant="outlined" 
              sx={{ 
                ml: 2,
                borderColor: '#dee2e6',
                color: '#6c757d',
                fontSize: '0.65rem', 
                height: '20px', 
                fontWeight: 400
              }}
            />
          </Box>
        </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                API permissions determine what resources your app can access on behalf of a user or itself.
              </Typography>
              
              {/* Visual explanation of permission types */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#ffffff', 
                border: '1px solid #e0e0ff', 
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                  Understanding Permission Types:
                </Typography>
                <Typography variant="body2">
                  • <strong>Delegated permissions</strong>: Used when your app accesses an API with a signed-in user present<br />
                  • <strong>Application permissions</strong>: Used when your app runs as a background service without a user present<br />
                  • <strong>Custom API permissions</strong>: Used to access other registered applications in your tenant
                </Typography>
              </Box>
              
              <Box sx={{
                p: 2,
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                  Important Considerations:
                </Typography>
                <Typography variant="body2">
                  • Admin consent is required for Application permissions and some sensitive Delegated permissions<br />
                  • "User.Read" is typically required by almost all applications<br />
                  • Each permission increases the scope of your application's access, so follow the principle of least privilege<br />
                  • Custom API permissions require the target API to have defined App Roles
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

      {/* ============================== */}
      {/* (D) Admin Consent Button (keep outside accordions) */}
      {/* ============================== */}
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button 
          variant="outlined" 
          onClick={grantAdminConsentForAllResources}
          startIcon={<SecurityIcon />}
        >
          Grant Admin Consent
        </Button>
        <Button 
          variant="contained" 
          color="success" 
          onClick={handleApplyChanges}
          startIcon={<SaveIcon />}
          sx={{ 
            px: 3,
            py: 1,
            fontSize: '1rem'
          }}
        >
          Apply Changes
        </Button>
      </Stack>
  
      {/* Keep your existing dialogs */}
      {/* Graph Permissions Picker Dialog */}
      <GraphPermissionsPicker
        open={graphPermsOpen}
        onClose={() => setGraphPermsOpen(false)}
        onSubmit={handleGraphPermsSubmit}
        graphAccessToken={graphAccessToken}
      />
  
      {/* Graph API Manual Entry Dialog */}
      <Dialog open={graphApiDialogOpen} onClose={() => setGraphApiDialogOpen(false)}>
        <DialogTitle>Add Graph API Permissions Manually</DialogTitle>
        <DialogContent>
          <TextField
            label="Graph Resource AppId"
            value={newApiResourceAppId}
            onChange={(e) => setNewApiResourceAppId(e.target.value)}
            fullWidth
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g. 00000003-0000-0000-c000-000000000000"
          />
          <TextField
            label="Graph Scope/Role Id"
            value={newApiScopeId}
            onChange={(e) => setNewApiScopeId(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="Paste scope or role GUID"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGraphApiDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              handleAddGraphApiPermission();
              setGraphApiDialogOpen(false);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
  
      {/* Custom API Dialog */}
      <Dialog 
        open={customApiDialogOpen} 
        onClose={() => {
          setCustomApiDialogOpen(false);
          setAvailableScopes([]);
          setCustomApiResourceAppId("");
          setCustomApiScopeId("");
          setLookupApiId("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Custom API Permission</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Step 1: Find the API by entering its App ID
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Resource AppId (GUID)"
                value={lookupApiId}
                onChange={(e) => setLookupApiId(e.target.value)}
                placeholder="e.g. 8caaee2d-c075-450d-9018-23496dd1456d"
              />
              <Button 
                variant="outlined" 
                onClick={lookupScopesForApi}
                disabled={!lookupApiId || isLookingUpScopes}
              >
                {isLookingUpScopes ? "Finding..." : "Find Scopes"}
              </Button>
            </Box>
          </Box>
          
          {availableScopes.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Step 2: Select the permission type and ID
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Permission Type:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant={customScopeType === "Scope" ? "contained" : "outlined"} 
                    onClick={() => setCustomScopeType("Scope")}
                    sx={{ flex: 1 }}
                  >
                    Delegated (Scope)
                  </Button>
                  <Button 
                    variant={customScopeType === "Role" ? "contained" : "outlined"} 
                    onClick={() => setCustomScopeType("Role")}
                    sx={{ flex: 1 }}
                  >
                    Application (Role)
                  </Button>
                </Box>
              </Box>
              
              <TextField
                select
                fullWidth
                label={`Available ${customScopeType === "Scope" ? "Delegated Permissions" : "Application Roles"}`}
                value={customApiScopeId}
                onChange={(e) => setCustomApiScopeId(e.target.value)}
                sx={{ mb: 2 }}
                helperText="Select a permission from the list (these are the GUID IDs required by Graph API)"
              >
                {availableScopes
                  .filter(s => s.type === customScopeType)
                  .map(scope => (
                    <MenuItem key={scope.id} value={scope.id}>
                      {scope.value} ({scope.id})
                    </MenuItem>
                  ))}
              </TextField>
            </>
          )}
          
          {availableScopes.length > 0 && customApiScopeId && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Selected Permission</AlertTitle>
              <Typography variant="body2">
                <strong>Resource AppId:</strong> {customApiResourceAppId}<br />
                <strong>Permission Type:</strong> {customScopeType}<br />
                <strong>Permission ID:</strong> {customApiScopeId}<br />
              </Typography>
            </Alert>
          )}
          
          {availableScopes.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>How Custom API Permissions Work</AlertTitle>
              <Typography variant="body2">
                1. Enter the API's App ID (GUID) above and click "Find Scopes"<br />
                2. The system will look up available permissions for that API<br />
                3. Select the specific permission to add to your application<br /><br />
                <strong>Note:</strong> You need to use the permission's GUID ID, not its string value
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCustomApiDialogOpen(false);
            setAvailableScopes([]);
            setCustomApiResourceAppId("");
            setCustomApiScopeId("");
            setLookupApiId("");
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddCustomApiPermission}
            disabled={!customApiResourceAppId || !customApiScopeId}
          >
            Add Permission
          </Button>
        </DialogActions>
      </Dialog>
    </CardContent>
  </Card>
  );
};

export default ApiPermissionsManager;
